import { sanitizeText } from '@shared/utils/security';

export class LoadingManager {
  private overlay!: HTMLElement;
  private spinner!: HTMLElement;
  private text!: HTMLElement;
  private isActive = false;

  constructor() {
    this.createOverlay();
  }

  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.className = 'ce-loading-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 9999999997;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    `;

    // Create container for spinner and text
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    `;

    this.spinner = document.createElement('div');
    this.spinner.className = 'ce-loading-spinner';
    this.spinner.style.cssText = `
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.3);
      border-top: 3px solid #fff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;

    this.text = document.createElement('div');
    this.text.className = 'ce-loading-text';
    this.text.style.cssText = `
      color: #fff;
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
      font-size: 16px;
      font-weight: 500;
      text-align: center;
    `;

    // Add spinner animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    container.appendChild(this.spinner);
    container.appendChild(this.text);
    this.overlay.appendChild(container);
    document.documentElement.appendChild(this.overlay);
  }

  show(
    message: string = 'Loading...',
    options?: { timeout?: number; allowInteraction?: boolean },
  ): void {
    if (this.isActive) return;

    this.isActive = true;
    this.text.textContent = sanitizeText(message);

    const timeout = options?.timeout;
    const allowInteraction = options?.allowInteraction !== false;

    this.overlay.style.pointerEvents = allowInteraction ? 'auto' : 'none';
    this.overlay.style.opacity = '1';

    if (timeout) {
      setTimeout(() => this.hide(), timeout);
    }
  }

  hide(): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.overlay.style.opacity = '0';

    setTimeout(() => {
      this.overlay.style.pointerEvents = 'none';
    }, 300);
  }

  setText(message: string): void {
    this.text.textContent = sanitizeText(message);
  }

  isActiveLoading(): boolean {
    return this.isActive;
  }

  destroy(): void {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.remove();
    }
  }
}
